import React, { useEffect } from 'react';
import { ChartCard, HorizontalBars, InfoTooltip } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import {
  type RelativePerformance,
  getRelativePerformance,
} from '../-helpers/getRelativePerformance';
import { useSortedChartData } from '../-hooks/useSortedChartData';
import { OptionalKey } from '../route';

export const KPIImpact_PatientsSavingPremiumFragment = gql(/* GraphQL */ `
  fragment KPIImpact_PatientsSavingPremiumFragment on HealthcareIndicators {
    patientsSavingPremium {
      trimmedAvg
      isDataSuppressed
    }
  }
`);

type PatientsSavingPremiumProps = {
  className?: string;
  indicators: FragmentType<typeof KPIImpact_PatientsSavingPremiumFragment>;
  viewerIndicators?: FragmentType<typeof KPIImpact_PatientsSavingPremiumFragment>;
  investmentLevel: OptionalKey;
  onEvaluatePerformance: (key: string, performance: RelativePerformance) => void;
};

export function PatientsSavingPremium({
  indicators,
  viewerIndicators,
  investmentLevel,
  onEvaluatePerformance,
  className,
}: PatientsSavingPremiumProps) {
  const benchmarkData = useFragment(KPIImpact_PatientsSavingPremiumFragment, indicators);
  const viewerData = useFragment(KPIImpact_PatientsSavingPremiumFragment, viewerIndicators);

  const benchmarkDataMap = {
    company: benchmarkData.patientsSavingPremium,
    investment: benchmarkData.patientsSavingPremium,
    rate_of_change: benchmarkData.patientsSavingPremium,
  };

  const viewerDataMap = {
    company: viewerData?.patientsSavingPremium,
    investment: viewerData?.patientsSavingPremium,
    rate_of_change: viewerData?.patientsSavingPremium,
  };

  const selectedBenchmarkData = benchmarkDataMap[investmentLevel];
  const selectedViewerData = viewerDataMap[investmentLevel];

  const { t, title, titleTooltipKPI } = useChartTranslations('patientsSavingPremium');

  const getDisplayValue = (value?: number | null): string => {
    if (value == null) return '';
    const strValue = (value * 100).toLocaleString('en', { maximumFractionDigits: 0 });
    return t('displayValue', { value: strValue }) ?? strValue;
  };

  const { sortedChartData } = useSortedChartData({
    selectedBenchmarkData,
    selectedViewerData,
    getDisplayValue,
  });

  const relativePerformance = getRelativePerformance(
    selectedBenchmarkData?.trimmedAvg,
    selectedViewerData?.trimmedAvg
  );

  useEffect(() => {
    onEvaluatePerformance('patientsSavingPremium', relativePerformance);
  }, [onEvaluatePerformance, relativePerformance]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header className="flex items-center justify-between">
        <ChartCard.Title>
          {title}
          {titleTooltipKPI && <InfoTooltip>{titleTooltipKPI}</InfoTooltip>}
        </ChartCard.Title>
        {/* <Button variant="outline" className="shrink-0 font-semibold uppercase">
          view details
        </Button> */}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={selectedBenchmarkData.isDataSuppressed}>
        <HorizontalBars min={0} max={1} showInlineDisplayValue={true}>
          {sortedChartData.map((data, index) => (
            <HorizontalBars.Item
              key={index}
              label={data.label}
              value={data.value}
              displayValue={data.displayValue}
              color={data.color}
              isDashed={data.isDashed}
            />
          ))}
        </HorizontalBars>

        {/* <Link href="/impact-kpis/land-managed" className="mt-5 lg:hidden">
          <Button variant="outline" size="small" className="shrink-0 font-semibold uppercase">
            view details
          </Button>
        </Link> */}
      </ChartCard.Body>
    </ChartCard>
  );
}
